import type { AdjustmentKind } from '../model/types';
import { clampByte, luma, type PixelBuffer } from './buffer';
import { applyLut } from './curves';

/**
 * Pointwise colour adjustments.
 *
 * Every one of these is a pure function of a pixel and a normalised parameter in
 * -1..1, which is what makes them unit-testable without a canvas and what lets the
 * UI show a live preview by simply re-running them.
 *
 * Alpha is never touched: an adjustment must not make a transparent pixel opaque
 * or a visible one vanish.
 *
 * None of them allocate per pixel. A preview re-runs the whole filter on every
 * slider tick, and a 2048² layer is 4.19 million pixels a go — the tuple-per-pixel
 * callback these used to ride on made a brightness 26× slower than the same-sized
 * LUT pass. The three per-channel affine maps go through a 256-entry table built
 * once per application (the same shape that makes levels and curves cheap); the
 * rest are direct loops with scalar locals, and the HSL round-trip is inlined
 * rather than paid for in two arrays per pixel.
 */

/** A per-channel affine map as a 256-entry table, so the loop is three lookups. */
function affineLut(map: (level: number) => number): Uint8Array {
  const lut = new Uint8Array(255 + 1);
  for (let level = 0; level <= 255; level += 1) {
    lut[level] = clampByte(map(level));
  }
  return lut;
}

/** Additive lift or drop. `value` -1..1 maps to ±128 levels. */
export function applyBrightness(buffer: PixelBuffer, value: number): void {
  const offset = value * 128;
  applyLut(
    buffer,
    affineLut((level) => level + offset),
  );
}

/** Scale around mid-grey. Uses the classic contrast factor so the curve is symmetric. */
export function applyContrast(buffer: PixelBuffer, value: number): void {
  const c = value * 255;
  const factor = (259 * (c + 255)) / (255 * (259 - c));
  applyLut(
    buffer,
    affineLut((level) => factor * (level - 128) + 128),
  );
}

/** Photographic stops: 2^ev. `value` -1..1 maps to -2..+2 stops. */
export function applyExposure(buffer: PixelBuffer, value: number): void {
  const gain = 2 ** (value * 2);
  applyLut(
    buffer,
    affineLut((level) => level * gain),
  );
}

/** Mix each channel toward its own luma. -1 is fully desaturated, +1 doubles. */
export function applySaturation(buffer: PixelBuffer, value: number): void {
  const scale = 1 + value;
  const { data } = buffer;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const grey = luma(r, g, b);
    data[i] = clampByte(grey + (r - grey) * scale);
    data[i + 1] = clampByte(grey + (g - grey) * scale);
    data[i + 2] = clampByte(grey + (b - grey) * scale);
  }
}

/**
 * Rotate hue. `value` -1..1 maps to ±180°, so the slider ends meet in the middle.
 *
 * The RGB→HSL→RGB round-trip is the original pair of helpers inlined with the
 * same operation order, so the numbers are identical — only the allocations are
 * gone. Greys keep their early out.
 */
export function applyHue(buffer: PixelBuffer, value: number): void {
  const degrees = value * 180;
  const { data } = buffer;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    const rd = r / 255;
    const gd = g / 255;
    const bd = b / 255;
    const max = Math.max(rd, gd, bd);
    const min = Math.min(rd, gd, bd);
    const delta = max - min;
    if (delta === 0) continue; // greys have no hue to rotate
    const lightness = (max + min) / 2;
    const saturation = delta / (1 - Math.abs(2 * lightness - 1));
    const rawHue =
      max === rd
        ? (((gd - bd) / delta) % 6) * 60
        : max === gd
          ? ((bd - rd) / delta + 2) * 60
          : ((rd - gd) / delta + 4) * 60;
    const hue = (rawHue + 360) % 360; // rgbToHsl's normalisation
    const h = (((hue + degrees) % 360) + 360) % 360; // hslToRgb's normalisation
    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lightness - chroma / 2;
    let r1 = 0;
    let g1 = 0;
    let b1 = 0;
    if (h < 60) {
      r1 = chroma;
      g1 = x;
    } else if (h < 120) {
      r1 = x;
      g1 = chroma;
    } else if (h < 180) {
      g1 = chroma;
      b1 = x;
    } else if (h < 240) {
      g1 = x;
      b1 = chroma;
    } else if (h < 300) {
      r1 = x;
      b1 = chroma;
    } else {
      r1 = chroma;
      b1 = x;
    }
    // Assigned raw, exactly as the helpers left it: the clamped array rounds
    // and clamps on assignment.
    data[i] = (r1 + m) * 255;
    data[i + 1] = (g1 + m) * 255;
    data[i + 2] = (b1 + m) * 255;
  }
}

export function applyGrayscale(buffer: PixelBuffer): void {
  const { data } = buffer;
  for (let i = 0; i < data.length; i += 4) {
    const grey = luma(data[i] ?? 0, data[i + 1] ?? 0, data[i + 2] ?? 0);
    data[i] = grey;
    data[i + 1] = grey;
    data[i + 2] = grey;
  }
}

/** The usual sepia matrix — warm, and deliberately not a hue rotation. */
export function applySepia(buffer: PixelBuffer): void {
  const { data } = buffer;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] ?? 0;
    const g = data[i + 1] ?? 0;
    const b = data[i + 2] ?? 0;
    data[i] = clampByte(0.393 * r + 0.769 * g + 0.189 * b);
    data[i + 1] = clampByte(0.349 * r + 0.686 * g + 0.168 * b);
    data[i + 2] = clampByte(0.272 * r + 0.534 * g + 0.131 * b);
  }
}

export function applyInvert(buffer: PixelBuffer): void {
  const { data } = buffer;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255 - (data[i] ?? 0);
    data[i + 1] = 255 - (data[i + 1] ?? 0);
    data[i + 2] = 255 - (data[i + 2] ?? 0);
  }
}

/** Apply an adjustment by kind. `value` is ignored for the parameterless ones. */
export function applyAdjustment(buffer: PixelBuffer, kind: AdjustmentKind, value: number): void {
  switch (kind) {
    case 'brightness':
      applyBrightness(buffer, value);
      break;
    case 'contrast':
      applyContrast(buffer, value);
      break;
    case 'exposure':
      applyExposure(buffer, value);
      break;
    case 'saturation':
      applySaturation(buffer, value);
      break;
    case 'hue':
      applyHue(buffer, value);
      break;
    case 'grayscale':
      applyGrayscale(buffer);
      break;
    case 'sepia':
      applySepia(buffer);
      break;
    case 'invert':
      applyInvert(buffer);
      break;
    default:
      // blur and sharpen are convolution filters and live in ./blur
      break;
  }
}
